// =============================================================
// streamCheck.js — reading the streaming self-test's result.
//
// The server emits frames on a fixed cadence; only the BROWSER can tell whether
// they were forwarded one by one or held and delivered together, because
// buffering looks identical from the server side. So the verdict is a function
// of arrival times, and it lives here where it can be unit-tested rather than
// inside a click handler.
//
// Mirrors the constants in api/src/streamCheck.js, but takes them off the
// probe-start frame at runtime rather than hardcoding them — the server is the
// authority on its own cadence.
// =============================================================

/** Fraction of the expected spread above which the stream is clearly live. */
const LIVE_RATIO     = 0.6;
/** Fraction below which it clearly arrived in one lump. */
const BUFFERED_RATIO = 0.25;

/**
 * Turn the recorded arrivals into a verdict.
 *
 * @param {object}   probe
 * @param {number[]} probe.arrivals       ms since the request started, one per probe frame
 * @param {number}   probe.expectedSpreadMs  what a forwarded stream would show
 * @param {boolean} [probe.streamed]      false when the response wasn't SSE at all
 * @returns {{verdict, headline, detail, spreadMs, firstFrameMs}}
 */
export function readStreamProbe({ arrivals = [], expectedSpreadMs = 0, streamed = true } = {}) {
  if (!streamed) {
    return {
      verdict: 'not-streamed',
      headline: 'The response came back as ordinary JSON',
      detail: 'Nothing streamed at all. Either streaming is switched off for this environment, or something in front of the API converted the response.',
      spreadMs: 0, firstFrameMs: null,
    };
  }
  if (arrivals.length < 2) {
    return {
      verdict: 'inconclusive',
      headline: 'Not enough frames arrived to judge',
      detail: `Expected several frames, saw ${arrivals.length}. Try again; if it keeps happening the stream is being cut short.`,
      spreadMs: 0, firstFrameMs: arrivals[0] ?? null,
    };
  }

  const firstFrameMs = arrivals[0];
  const spreadMs = arrivals[arrivals.length - 1] - arrivals[0];
  const ratio = expectedSpreadMs > 0 ? spreadMs / expectedSpreadMs : 0;

  if (ratio >= LIVE_RATIO) {
    return {
      verdict: 'live',
      headline: 'Streaming works end to end',
      detail: `Frames arrived ${Math.round(spreadMs)}ms apart, close to the ${Math.round(expectedSpreadMs)}ms the server spaced them by — nothing in between is buffering. Safe to turn streaming on in production.`,
      spreadMs, firstFrameMs,
    };
  }
  if (ratio <= BUFFERED_RATIO) {
    return {
      verdict: 'buffered',
      headline: 'Something in front of the API is buffering',
      detail: `The server spread the frames over ${Math.round(expectedSpreadMs)}ms, but they all landed within ${Math.round(spreadMs)}ms of each other — so they were held and released together. Chat would still work, it just wouldn't stream.`,
      spreadMs, firstFrameMs,
    };
  }
  return {
    verdict: 'inconclusive',
    headline: 'Partly buffered, or a slow connection',
    detail: `Frames spread over ${Math.round(spreadMs)}ms against an expected ${Math.round(expectedSpreadMs)}ms. Run it again — a congested network can look like this.`,
    spreadMs, firstFrameMs,
  };
}

/** Colour key for the verdict, so the UI doesn't re-decide what it means. */
export const VERDICT_TONE = {
  live: 'good', buffered: 'bad', 'not-streamed': 'bad', inconclusive: 'warn',
};
